import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@components/Button';
import Modal from '@components/Modal';
import {
  deleteBlogPost,
  fetchBlogPosts,
  toggleBlogPostStatus,
  type BlogPost,
} from '@lib/api';

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function sortPosts(list: BlogPost[]): BlogPost[] {
  const copy = [...list];
  copy.sort((a, b) => {
    if (a.is_published !== b.is_published) {
      return a.is_published ? -1 : 1;
    }
    const dateA = new Date(a.updated_at ?? a.created_at).getTime();
    const dateB = new Date(b.updated_at ?? b.created_at).getTime();
    return dateB - dateA;
  });
  return copy;
}

function ActionsMenu(props: {
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { items } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onEsc);
    }
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">Actions</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminBlog() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BlogPost | null>(null);

  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchBlogPosts(true);
        if (!active) return;
        setPosts(sortPosts(data));
      } catch (err) {
        if (!active) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to load blog posts right now.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(search.trim().toLowerCase()),
      250
    );
    return () => clearTimeout(handle);
  }, [search]);

  const stats = useMemo(() => {
    const total = posts.length;
    const activeCount = posts.filter((p) => p.is_published).length;
    return {
      total,
      active: activeCount,
      drafts: total - activeCount,
    };
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (!debouncedSearch) return sortPosts(posts);
    return sortPosts(
      posts.filter((post) => {
        const haystack =
          `${post.title} ${post.slug} ${post.excerpt ?? ''} ${post.content}`.toLowerCase();
        return haystack.includes(debouncedSearch);
      })
    );
  }, [posts, debouncedSearch]);

  const goToEditPost = (post: BlogPost) => {
    navigate(`/admin/blog/${post.id}/edit`);
  };

  const previewPost = (post: BlogPost) => {
    if (!post.slug) {
      setError('Cannot preview this post because it is missing a slug.');
      return;
    }
    const previewFlag =
      post.is_published || post.published_at ? '' : '?preview=1';
    const url = `/blog/${post.slug}${previewFlag}`;
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  const openDeleteModal = (post: BlogPost) => {
    setDeleteTarget(post);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleToggleStatus = async (post: BlogPost) => {
    setTogglingId(post.id);
    setError(null);
    try {
      const updated = await toggleBlogPostStatus(post.id, !post.is_published);
      // rebuild sitemap when a post goes live
      try {
        void fetch('/api/regenerate-sitemap', { method: 'POST' });
      } catch {
        // ignore
      }
      setPosts((prev) =>
        sortPosts(prev.map((p) => (p.id === post.id ? updated : p)))
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to update status. Please try again.'
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      const wasPublished = deleteTarget.is_published;
      await deleteBlogPost(deleteTarget.id);
      // if a published post was removed, regenerate sitemap
      if (wasPublished) {
        try {
          void fetch('/api/regenerate-sitemap', { method: 'POST' });
        } catch {
          // best-effort
        }
      }
      setPosts((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(
        err instanceof Error
          ? err.message
          : 'Failed to delete the post. Please try again.'
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-100 via-indigo-50 to-emerald-50">
      <div
        className="absolute inset-0 animate-[pulse_10s_ease-in-out_infinite] bg-gradient-to-br from-indigo-200/40 via-white/40 to-emerald-200/40"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 py-12">
        <div className="mb-8 text-center">
          <h1 className="gradient-text mb-3 pb-5 text-4xl font-bold tracking-tight">
            Blog Posts (Admin)
          </h1>
          <p className="text-lg text-gray-600">
            Create, edit, and publish new stories for the Xhalin community.
          </p>
        </div>

        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-indigo-100 bg-white/80 p-4 shadow-sm">
            <p className="text-sm text-gray-500">Total Posts</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {stats.total}
            </p>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-white/80 p-4 shadow-sm">
            <p className="text-sm text-gray-500">Active</p>
            <p className="mt-2 text-3xl font-bold text-emerald-600">
              {stats.active}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white/80 p-4 shadow-sm">
            <p className="text-sm text-gray-500">Drafts</p>
            <p className="mt-2 text-3xl font-bold text-slate-600">
              {stats.drafts}
            </p>
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:max-w-md">
            <label htmlFor="blog-search" className="sr-only">
              Search posts
            </label>
            <div className="relative">
              <input
                id="blog-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search posts by title, slug, or text..."
                className="w-full rounded-2xl border border-gray-200 bg-white/80 px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <svg
                className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" strokeLinecap="round" />
              </svg>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => navigate('/admin/blog/new')}
              className="!rounded-lg !bg-indigo-600 !px-6 !py-2.5 !text-white shadow-lg shadow-indigo-200 hover:!bg-indigo-500 focus:!ring-indigo-400"
            >
              + Create New Post
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50/80 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="rounded-3xl bg-white/80 p-6 shadow-xl backdrop-blur">
          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2].map((skeleton) => (
                <div
                  key={skeleton}
                  className="h-24 animate-pulse rounded-2xl bg-gray-100/60"
                />
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white/70 px-6 py-12 text-center text-gray-600">
              {debouncedSearch
                ? 'No posts match your search.'
                : 'No blog posts yet. Create the first story to get started.'}
            </div>
          ) : (
            <>
              <div className="space-y-4 lg:hidden">
                {filteredPosts.map((post) => (
                  <div
                    key={post.id}
                    className={`rounded-2xl border bg-white p-5 shadow-sm ${post.is_published ? 'border-emerald-200 ring-1 ring-emerald-100' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-lg">
                          {post.slug ? (
                            <a
                              href={`/blog/${post.slug}${post.is_published || post.published_at ? '' : '?preview=1'}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-left font-semibold text-sky-600 no-underline focus:outline-none"
                              aria-label={`Open ${post.title} in new tab`}
                            >
                              {post.title}
                            </a>
                          ) : (
                            <span className="text-left font-semibold text-gray-900">
                              {post.title}
                            </span>
                          )}
                        </h2>
                        <p className="text-xs text-gray-500">{post.slug}</p>
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold ${post.is_published ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'}`}
                      >
                        {post.is_published ? 'Active' : 'Draft'}
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-gray-600">
                      {post.excerpt || 'No excerpt provided.'}
                    </p>
                    <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                      <div>
                        Updated {formatDate(post.updated_at)} |{' '}
                        {post.published_at
                          ? `Published ${formatDate(post.published_at)}`
                          : 'Not published'}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(post)}
                        disabled={togglingId === post.id}
                        className={`text-xs font-semibold ${post.is_published ? 'text-emerald-600' : 'text-gray-500'} underline-offset-2 hover:underline disabled:cursor-not-allowed`}
                      >
                        {togglingId === post.id
                          ? 'Updating...'
                          : post.is_published
                            ? 'Set Draft'
                            : 'Publish'}
                      </button>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <ActionsMenu
                        items={[
                          {
                            key: 'preview',
                            label: 'Preview',
                            className: '!text-sky-600',
                            onClick: () => previewPost(post),
                            disabled: !post.slug,
                          },
                          {
                            key: 'edit',
                            label: 'Edit',
                            className: '!text-indigo-600',
                            onClick: () => goToEditPost(post),
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            className: '!text-rose-600',
                            onClick: () => openDeleteModal(post),
                          },
                        ]}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden lg:block">
                <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                  <table className="min-w-full text-left text-sm text-gray-800">
                    <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Title</th>
                        <th className="px-4 py-3">Slug</th>
                        <th className="px-4 py-3">Excerpt</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Published</th>
                        <th className="px-4 py-3">Updated</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPosts.map((post) => (
                        <tr
                          key={post.id}
                          className={`border-t border-gray-100 ${post.is_published ? 'bg-emerald-50/60' : 'bg-gray-50/50'} transition hover:bg-indigo-50/70`}
                        >
                          <td className="px-4 py-4 align-top">
                            <div className="font-semibold text-gray-900">
                              {post.slug ? (
                                <a
                                  href={`/blog/${post.slug}${post.is_published || post.published_at ? '' : '?preview=1'}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-left text-sm font-semibold text-sky-600 no-underline focus:outline-none"
                                  aria-label={`Open ${post.title} in new tab`}
                                >
                                  {post.title}
                                </a>
                              ) : (
                                <span className="text-left text-sm font-semibold text-gray-900">
                                  {post.title}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              {post.cover_image
                                ? 'Has cover image'
                                : 'No cover image'}
                            </p>
                          </td>
                          <td className="px-4 py-4 align-top text-gray-900">
                            {post.slug}
                          </td>
                          <td className="px-4 py-4 align-top text-gray-700">
                            <div className="max-w-xs truncate">
                              {post.excerpt || 'No excerpt provided.'}
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top">
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={post.is_published}
                                onClick={() => handleToggleStatus(post)}
                                disabled={togglingId === post.id}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full transition ${post.is_published ? 'border border-emerald-400 bg-emerald-500 shadow-inner' : 'border border-gray-200 bg-gray-300'} ${togglingId === post.id ? 'opacity-70' : ''}`}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full transition ${post.is_published ? 'translate-x-5 bg-emerald-600 shadow-md ring-1 ring-emerald-200' : 'translate-x-1 border border-rose-300 bg-rose-500 shadow-sm'}`}
                                />
                                <span className="sr-only">Toggle status</span>
                              </button>
                              <span
                                className={`text-sm font-semibold ${post.is_published ? 'text-emerald-700' : 'text-gray-500'}`}
                              >
                                {togglingId === post.id
                                  ? 'Updating...'
                                  : post.is_published
                                    ? 'Active'
                                    : 'Draft'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 align-top text-gray-900">
                            {post.published_at
                              ? formatDate(post.published_at)
                              : '--'}
                          </td>
                          <td className="px-4 py-4 align-top text-gray-900">
                            {formatDate(post.updated_at)}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end">
                              <ActionsMenu
                                items={[
                                  {
                                    key: 'preview',
                                    label: 'Preview',
                                    className: '!text-sky-600',
                                    onClick: () => previewPost(post),
                                    disabled: !post.slug,
                                  },
                                  {
                                    key: 'edit',
                                    label: 'Edit',
                                    className: '!text-indigo-600',
                                    onClick: () => goToEditPost(post),
                                  },
                                  {
                                    key: 'delete',
                                    label: 'Delete',
                                    className: '!text-rose-600',
                                    onClick: () => openDeleteModal(post),
                                  },
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {deleteModalOpen && deleteTarget && (
        <Modal
          title="Delete Post"
          onClose={closeDeleteModal}
          className="max-w-lg"
        >
          <p className="text-sm text-gray-700">
            Are you sure you want to permanently delete{' '}
            <span className="font-semibold">{deleteTarget.title}</span>? This
            action cannot be undone.
          </p>
          {deleteError && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {deleteError}
            </div>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={closeDeleteModal}
              disabled={deleteSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={deleteSubmitting}
              className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
            >
              {deleteSubmitting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AdminBlog;
